/**
 * Moving a fighter around the other one.
 *
 * A fighter's move vector is read relative to their opponent, which is what
 * makes it mean anything: there is no "north". Push +y and you walk *at* them,
 * push -y and you back off, push x and you circle them. Forward is therefore
 * recomputed every frame from where the opponent actually is, not from the
 * fighter's own rotation.
 *
 * Walking in and backing off are separate states — `walk` and `walkBack` —
 * because they are separate animations, and the state graph is this project's
 * record of what a fighter is doing. Circling counts as walking in: a sidestep
 * is thrown forward-footed.
 *
 * Facing is a different question, and the state graph answers it. A fighter
 * turns to their opponent only in the states that declare `faces` — standing,
 * moving, guarding. Every other state freezes their aim where it was, which is
 * what lets a punch be made to miss: the attacker commits to a direction, and
 * the defender has the length of the strike to not be in it.
 *
 * Movement is a *request*, not a fact. The desired state goes to the state
 * machine first and the fighter only moves if it was accepted — so a fighter
 * committed to a punch or stuck in a stagger stays put instead of sliding
 * around underneath their own animation. That gate is the whole point of this
 * module living in `core`: it holds no three.js, and the rule can be tested
 * without one.
 *
 * Whose intent it is — a player's thumbstick or a timer's idea of "approach" —
 * this module does not care. It is handed a vector and told whether the run
 * button is down.
 */

export const MOVEMENT = Object.freeze({
  /** Metres per second at a walk. */
  walkSpeed: 1.6,
  runSpeed: 3.4,
  /** Sidestepping is slower than walking straight in. */
  strafeScale: 0.75,
  /** And backing out of range is slower still. */
  backScale: 0.68,
  /**
   * How much of the push has to be *forward* before a run is allowed. You can
   * sprint at someone; you cannot sprint backwards out of trouble, which is
   * what makes committing to the approach cost something.
   */
  runThreshold: 0.55,
  /** Below this the stick is resting and the fighter stands still. */
  deadzone: 0.08,
  /**
   * How wide a fighter is, for anything that has to bump them into something.
   * Two of these is the closest a pair can stand, which is not a coincidence —
   * `minSeparation` is two bodies touching.
   */
  bodyRadius: 0.31,
  /** Closest two fighters may stand, chest to chest. */
  minSeparation: 0.62,
  /** Nobody leaves this circle. Keeps the pair inside the camera's reach. */
  arenaRadius: 4.5,
  /**
   * Where a fighter may walk, when the arena has an opinion. `null` falls back
   * to the circle of `arenaRadius`, which is what every arena had first.
   *
   * This is the walkable floor, not the arena: it stops well short of anything
   * hard on every side, because **the arena is a thing you do to somebody, not
   * a thing you bump into**. Nobody walks into a wall, a pillar or a track pit
   * — only a fighter who has been *sent* reaches one.
   */
  bounds: null,
});

export function createLocomotion({ combatant, opponent, ...overrides }) {
  const config = { ...MOVEMENT, ...overrides };
  const { machine } = combatant;

  /**
   * Last known forward. Two fighters standing on the same spot leave no
   * direction to derive, and a zero forward would teleport them both to NaN.
   */
  let forward = { x: 0, z: -1 };

  function aim() {
    const self = combatant.position;
    const other = opponent.position;
    const dx = other.x - self.x;
    const dz = other.z - self.z;
    const gap = Math.hypot(dx, dz);
    if (gap > 1e-4) forward = { x: dx / gap, z: dz / gap };
    return gap;
  }

  /**
   * Turns to the opponent, if the current state allows it. Returns the gap.
   *
   * Separate from `update` and safe to call twice, because a control layer has
   * to aim *before* it decides to punch. Read the other way round, a fighter
   * would commit to the direction they were facing on the previous frame — and
   * the whole point of locking the aim is that the frame of commitment is the
   * one that counts.
   */
  function face() {
    const gap = aim();
    if (machine.definition.faces === true) combatant.faceTowards(opponent.position);
    return gap;
  }

  return {
    get forward() {
      return forward;
    },
    /** Whether the current state lets the fighter turn. */
    get tracking() {
      return machine.definition.faces === true;
    },
    config,
    face,

    /**
     * Swaps the floor out from under them.
     *
     * The arena is loadable at runtime, and a fighter carrying the previous
     * arena's walkable rectangle around is a fighter who can walk off the new
     * one — or who cannot reach half of it.
     */
    setBounds(bounds) {
      config.bounds = bounds ?? null;
      return config.bounds;
    },

    /**
     * One frame of movement. `move` is the input vector — +y toward the
     * opponent, +x to the fighter's right — `run` is whether the run verb is
     * held, and `guard` is whether block is.
     *
     * A guard is a locomotion state rather than a separate system because it
     * competes with the same three: a blocking fighter is planted, and the
     * machine is asked for it in the same breath as `walk` and `run`.
     *
     * Returns what happened, which is what the tests assert on.
     */
    update(dt, { move = { x: 0, y: 0 }, run = false, guard = false } = {}) {
      // Facing is gated by the state graph: a fighter turns while they are
      // standing, moving or guarding, and holds their aim while they are
      // committed to anything else.
      const gap = face();

      const push = Math.hypot(move.x, move.y);
      // Holding the guard plants the fighter. Blocking while walking is a thing
      // some games have and this one does not, yet.
      const wants = !guard && push > config.deadzone;
      // A run needs the verb *and* genuine forward intent, so leaning on the
      // run button while circling does not turn a sidestep into a sprint.
      const running = wants && run && move.y > config.runThreshold;
      // Backing off is its own state, not walking with a minus sign. The two
      // are different animations — a fighter retreating keeps their guard and
      // their eyes on the other one — and the graph is where the game records
      // that kind of difference, so `walkBack` is a state rather than a flag.
      // The threshold is the deadzone: a sidestep with a touch of drift in it
      // is still a sidestep.
      const backing = wants && move.y < -config.deadzone;
      const desired = guard
        ? 'block'
        : wants
          ? running
            ? 'run'
            : backing
              ? 'walkBack'
              : 'walk'
          : 'idle';

      // The machine is the authority. Re-entering the state you are already in
      // is refused as a no-op, so that case skips the request entirely.
      if (machine.state !== desired && !machine.request(desired)) {
        return { state: machine.state, desired, moved: 0, gap };
      }
      if (!wants) return { state: machine.state, desired, moved: 0, gap };

      // Walking in costs nothing; circling and retreating cost speed.
      const speed = running ? config.runSpeed : config.walkSpeed;
      const ahead = move.y >= 0 ? move.y : move.y * config.backScale;
      const across = move.x * config.strafeScale;
      const step = speed * dt;
      const from = { x: combatant.position.x, z: combatant.position.z };

      // The move is solved in polar coordinates *around the opponent*, because
      // that is what the input already means: +y closes the gap, x swings
      // around them. Walking the straight tangent instead would creep a little
      // further out on every frame, so holding sidestep would quietly back a
      // fighter out of range. An arc does not drift.
      //
      // Closing the gap is also where the minimum separation belongs: it is a
      // floor under the radius rather than a correction applied afterwards, so
      // a fighter walks up to chest range and stops there.
      const radius = Math.max(config.minSeparation, gap - ahead * step);
      const swing = -(across * step) / Math.max(radius, 1e-3);
      // From the opponent back out to us. `forward` is the other way, and is
      // already guarded against the two of them sharing a spot.
      const out = rotateY({ x: -forward.x, z: -forward.z }, swing);

      const target = {
        x: opponent.position.x + out.x * radius,
        z: opponent.position.z + out.z * radius,
      };

      place(combatant.position, clampToBounds(target, config.bounds, config.arenaRadius));
      // The arena edge wins over the radius, so pushing back out is the last
      // word: a fighter pinned against the wall must not be shoved through the
      // body standing in front of them.
      place(
        combatant.position,
        pushApart(combatant.position, opponent.position, config.minSeparation, forward)
      );

      const moved = Math.hypot(combatant.position.x - from.x, combatant.position.z - from.z);
      return { state: machine.state, desired, moved, gap };
    },
  };
}

function place(position, to) {
  position.x = to.x;
  position.z = to.z;
}

/** Rotation in the floor plane. Exact on length, which is what kills the drift. */
function rotateY(v, angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: v.x * cos - v.z * sin, z: v.x * sin + v.z * cos };
}

/**
 * Keeps a point inside the arena circle. Exported because a fighter can be
 * moved by things other than their own legs — knockback, in particular — and
 * all of them have to respect the same wall.
 */
export function clampToArena(point, radius = MOVEMENT.arenaRadius) {
  const distance = Math.hypot(point.x, point.z);
  if (distance <= radius) return point;
  const scale = radius / distance;
  return { x: point.x * scale, z: point.z * scale };
}

/**
 * Keeps a point inside whatever shape the arena declares it may walk in.
 *
 * A circle was right while every arena was a square room fought over from the
 * middle. A subway platform is neither: it is long and shallow, with a wall
 * down one side and a drop down the other, and a circle inscribed in it would
 * throw away most of its length. So bounds are data like everything else, and
 * an arena that says nothing still gets the circle.
 */
export function clampToBounds(point, bounds, radius = MOVEMENT.arenaRadius) {
  if (!bounds) return clampToArena(point, radius);
  if (bounds.shape === 'rect') {
    return {
      x: clamp(point.x, bounds.minX, bounds.maxX),
      z: clamp(point.z, bounds.minZ, bounds.maxZ),
    };
  }
  return clampToArena(point, bounds.radius ?? radius);
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Keeps a fighter out of the body in front of them by sliding them back onto
 * the minimum-separation circle — so walking into the opponent stops dead at
 * range rather than passing through them, and circling still works because only
 * the radial part is clamped.
 */
function pushApart(point, other, minimum, fallback) {
  const dx = point.x - other.x;
  const dz = point.z - other.z;
  const gap = Math.hypot(dx, dz);
  if (gap >= minimum) return point;
  // Exactly co-located: no direction to separate along, so back out the way we
  // came in rather than dividing by zero.
  const away = gap > 1e-4 ? { x: dx / gap, z: dz / gap } : { x: -fallback.x, z: -fallback.z };
  return { x: other.x + away.x * minimum, z: other.z + away.z * minimum };
}
